import React from "react";
import { ArrowRight, CircleCheck, Layers, Zap, ChartBar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const servicesData = [{
  title: "Co-Management and Band Advisory",
  description: "Comprehensive advisory services tailored for First Nations administration, focusing on governance excellence, policy development, and sustainable community leadership. We provide the stable framework necessary for long-term self-governance.",
  capabilities: ["Governance", "Financial Oversight", "Policy Development"],
  image: "https://stable-woodcock.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-May-9-2026-09_18_21-AM.png?v=nybaykxifn",
  icon: ChartBar
}, {
  title: "Financial Management and Controllership",
  description: "Precision controllership that provides clear financial visibility, ensuring your organization's resources are managed with professional rigor. We implement internal controls and cash flow management strategies that drive transparency.",
  capabilities: ["Reporting", "Internal Controls", "Cash Flow"],
  image: "https://stable-woodcock.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-May-9-2026-09_20_54-AM.png?v=lbvgf0y9mbj",
  icon: Layers
}, {
  title: "Audit Catch-Up and Financial Recovery",
  description: "Targeted recovery strategies designed to resolve audit backlogs and restore financial compliance. Our team prepares your records for year-end and remediates legacy issues to provide a clean slate for future growth.",
  capabilities: ["Year-End Prep", "Compliance", "Remediation"],
  image: "https://stable-woodcock.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-May-9-2026-09_25_10-AM.png?v=md44pqbean7",
  icon: CircleCheck
}, {
  title: "Project Planning and Delivery",
  description: "Expert management for complex capital projects and grant funding. We ensure deliverables are met on time and within budget, facilitating seamless implementation from initial grant application to final project reporting.",
  capabilities: ["Capital Projects", "Grant Management", "Implementation"],
  image: "https://stable-woodcock.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-May-9-2026-09_28_11-AM.png?v=jdvs296mk8f",
  icon: Zap
}];
export default function TailoredSolutions() {
  return <section data-section-id="3322" id="management-advisory-expertise" className="bg-background w-full overflow-hidden text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {servicesData.map((service, index) => <ServiceModule key={index} service={service} index={index} />)}
      </div>
    </section>;
}
function ServiceModule({
  service,
  index
}: {
  service: (typeof servicesData)[0];
  index: number;
}) {
  const isEven = index % 2 === 0;
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} className={cn("flex min-h-[80vh] flex-col items-center justify-center py-24 lg:py-0 border-b border-muted last:border-0 hover:bg-muted/30 transition-colors duration-700 ease-in-out", "transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
  <div className="grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-24">
    <div className={cn("w-full transition-all duration-1000 delay-300 ease-out", isEven ? "lg:order-1" : "lg:order-2", isVisible ? "opacity-100 translate-x-0" : isEven ? "opacity-0 -translate-x-12" : "opacity-0 translate-x-12")}>
      <div className="overflow-hidden rounded-lg shadow-md ring-1 ring-border">
        <AspectRatio ratio={4 / 3}>
          <img src={service.image} alt={`${service.title} support from RiverBend`} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
        </AspectRatio>
      </div>
    </div>

    <div className={cn("flex flex-col justify-center space-y-8", isEven ? "lg:order-2" : "lg:order-1")}>
      <div className="space-y-6">
        <div className="flex flex-wrap gap-3">
          {service.capabilities.map((cap, i) => <Badge key={i} variant="secondary" className="px-4 py-1.5 text-xs font-default font-medium tracking-widest uppercase text-secondary bg-secondary/10 hover:bg-secondary/20 border-secondary/10">
              {cap}
            </Badge>)}
        </div>

        <h2 className="font-default text-4xl font-semibold tracking-tight text-primary sm:text-5xl md:text-6xl md:leading-[1.1]">
          {service.title}
        </h2>

        <p className="font-sans text-lg leading-relaxed text-muted-foreground md:text-xl">
          {service.description}
        </p>

        <p className="font-sans text-base leading-relaxed text-muted-foreground/90 md:text-lg">
          We help clients understand their financial information, fix problems
          in their records, and build better systems for tracking money,
          programs, and reporting needs.
        </p>
      </div>

      <div className="pt-4">
        <Button asChild size="lg" className="rounded-sm px-8 text-base h-12 bg-primary text-primary-foreground hover:bg-primary/90">
          <a href="/contact/">
            Talk to RiverBend
            <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </Button>
      </div>
    </div>
  </div>
</div>;
}